/**
 * OwnerQrScanAnalytics — weekly QR scan chart for a business.
 *
 * Route: /food-pulse/owner/inventory/scans?business_id=…
 *
 * Aggregates SpLink click stats across every lot owned by the business
 * via GET /api/inventory/analytics/qr-scans. Shows:
 *   - big total-scans number for the selected window
 *   - simple SVG bar chart per day
 *   - top 10 lots sorted by scans
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, Loader2, QrCode, TrendingUp } from "lucide-react";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const GREEN  = "#0F3D2E";
const CREAM  = "#FBF7EF";
const AMBER  = "#E4A140";
const PURPLE = "#7C3AED";

function BarChart({ series }) {
  const max = Math.max(1, ...series.map((s) => s.count));
  const W = 720, H = 180, pad = 24;
  const bw = series.length > 0 ? (W - pad * 2) / series.length : 0;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-44" data-testid="scan-chart">
      <line x1={pad} y1={H - pad} x2={W - pad} y2={H - pad} stroke="#E9D8B4" />
      {series.map((s, i) => {
        const h = ((H - pad * 2) * s.count) / max;
        const x = pad + i * bw + bw * 0.15;
        const y = H - pad - h;
        return (
          <g key={s.date}>
            <rect x={x} y={y} width={bw * 0.7} height={h} rx={2} fill={s.count === max ? AMBER : GREEN} />
            {i % Math.max(1, Math.floor(series.length / 8)) === 0 && (
              <text x={x + bw * 0.35} y={H - 6} fontSize="9" textAnchor="middle" fill="#666">{s.date.slice(5)}</text>
            )}
          </g>
        );
      })}
    </svg>
  );
}

async function fetchBusinesses() {
  try {
    const r = await http.get("/businesses/mine");
    return r.data.items || r.data || [];
  } catch { return []; }
}

export default function OwnerQrScanAnalytics() {
  const { user, loading: authLoading } = useAuth();
  const [params, setParams] = useSearchParams();
  const [businesses, setBusinesses] = useState([]);
  const [businessId, setBusinessId] = useState(params.get("business_id") || "");
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (user) fetchBusinesses().then(setBusinesses); }, [user]);
  useEffect(() => {
    if (businesses.length > 0 && !businessId) {
      setBusinessId(businesses[0].id);
      setParams({ business_id: businesses[0].id });
    }
  }, [businesses, businessId, setParams]);

  const reload = async () => {
    if (!businessId) return;
    setLoading(true);
    try {
      const r = await http.get("/inventory/analytics/qr-scans", { params: { business_id: businessId, days } });
      setData(r.data);
    } catch (e) { console.warn(e); setData(null); }
    setLoading(false);
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [businessId, days]);

  const total = data?.total_scans ?? 0;
  const series = useMemo(() => data?.by_day || [], [data]);
  const topLots = useMemo(() => data?.top_lots || [], [data]);

  if (authLoading) return <div className="p-10 text-center"><Loader2 className="mx-auto animate-spin" /></div>;
  if (!user) return <div className="p-10 text-center">Please sign in to view scan analytics.</div>;

  return (
    <main className="min-h-screen" style={{ background: CREAM }} data-testid="qr-scans-page">
      <div className="max-w-5xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <div>
            <Link to={`/food-pulse/owner/inventory?business_id=${businessId}`} className="inline-flex items-center gap-1 text-xs font-semibold mb-2" style={{ color: GREEN }} data-testid="back-to-dashboard">
              <ArrowLeft size={12} /> Back to inventory
            </Link>
            <h1 className="text-2xl font-black inline-flex items-center gap-2" style={{ color: GREEN }}>
              <QrCode size={22} /> QR Scan Analytics
            </h1>
            <p className="text-sm text-gray-600">See which lots and labels shoppers are actually scanning.</p>
          </div>
          <div className="flex items-center gap-2">
            {businesses.length > 1 && (
              <select value={businessId} onChange={(e) => { setBusinessId(e.target.value); setParams({ business_id: e.target.value }); }} className="border rounded-full px-3 py-2 text-sm" data-testid="business-picker">
                {businesses.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            )}
            <select value={days} onChange={(e) => setDays(Number(e.target.value))} className="border rounded-full px-3 py-2 text-sm" data-testid="days-picker">
              <option value={7}>Last 7 days</option>
              <option value={14}>Last 14 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
            </select>
          </div>
        </div>

        {loading ? (
          <div className="p-10 text-center"><Loader2 className="mx-auto animate-spin" /></div>
        ) : !data ? (
          <div className="rounded-2xl border p-10 text-center bg-white" style={{ borderColor: "#E9D8B4" }} data-testid="analytics-empty">
            <div className="text-gray-600">No scan analytics available yet.</div>
          </div>
        ) : (
          <>
            <section className="grid gap-3 md:grid-cols-3 mb-6">
              <div className="rounded-2xl border p-4 bg-white" style={{ borderColor: "#E9D8B4" }}>
                <div className="text-xs text-gray-600">Total scans</div>
                <div className="text-3xl font-black" style={{ color: GREEN }} data-testid="total-scans">{total}</div>
                <div className="text-[11px] text-gray-500 mt-1">Across {data.lot_count} lots · last {data.days} days</div>
              </div>
              <div className="rounded-2xl border p-4 bg-white" style={{ borderColor: "#E9D8B4" }}>
                <div className="text-xs text-gray-600">Avg / day</div>
                <div className="text-3xl font-black" style={{ color: GREEN }}>
                  {series.length ? Math.round(total / series.length) : 0}
                </div>
              </div>
              <div className="rounded-2xl border p-4 bg-white" style={{ borderColor: "#E9D8B4" }}>
                <div className="text-xs text-gray-600 inline-flex items-center gap-1"><TrendingUp size={12} /> Peak day</div>
                <div className="text-3xl font-black" style={{ color: AMBER }}>
                  {series.length ? Math.max(...series.map((s) => s.count)) : 0}
                </div>
              </div>
            </section>

            <section className="rounded-2xl border p-4 bg-white mb-6" style={{ borderColor: "#E9D8B4" }} data-testid="scan-chart-section">
              <div className="text-sm font-bold mb-2" style={{ color: GREEN }}>Daily scans</div>
              {series.length > 0
                ? <BarChart series={series} />
                : <div className="text-xs text-gray-500 italic py-8 text-center">No scans recorded in this window yet.</div>}
            </section>

            <section className="rounded-2xl border p-4 bg-white" style={{ borderColor: "#E9D8B4" }} data-testid="top-lots-section">
              <div className="text-sm font-bold mb-3" style={{ color: GREEN }}>Top scanned lots</div>
              {topLots.length === 0 ? (
                <div className="text-xs text-gray-500 italic py-4">No lot-level scans in this window.</div>
              ) : (
                <ul className="divide-y" style={{ borderColor: "#E9D8B4" }}>
                  {topLots.map((l, i) => (
                    <li key={l.lot_id} className="py-2 flex items-center justify-between gap-3 text-sm" data-testid={`top-lot-${i}`}>
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="rounded-full w-6 h-6 flex items-center justify-center text-xs font-black text-white flex-shrink-0" style={{ background: i === 0 ? AMBER : GREEN }}>{i + 1}</span>
                        <div className="min-w-0">
                          <div className="font-semibold truncate" style={{ color: GREEN }}>{l.product_name || "—"}</div>
                          <div className="text-[11px] text-gray-500">Lot {l.lot_no || l.lot_id.slice(0, 6)}</div>
                        </div>
                      </div>
                      <div className="text-sm font-bold" style={{ color: PURPLE }}>{l.scans} scans</div>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </>
        )}
      </div>
    </main>
  );
}
